import { defineStore } from 'pinia'
import { computed, watch } from 'vue'
import { i18n, setLocale as setI18nLocale, type SupportedLocale } from '@/i18n'

export type Locale = string

const RTL_LOCALES = ['ar', 'he', 'fa', 'ur']

export const useLocaleStore = defineStore('locale', () => {
  const locale = computed(() => i18n.global.locale.value as string)

  const isRtl = computed(() => RTL_LOCALES.includes(locale.value))
  const dir = computed(() => (isRtl.value ? 'rtl' : 'ltr'))

  async function setLocale(newLocale: Locale): Promise<void> {
    await setI18nLocale(newLocale as SupportedLocale)
    applyDir(RTL_LOCALES.includes(newLocale) ? 'rtl' : 'ltr')
  }

  function applyDir(direction: 'rtl' | 'ltr'): void {
    document.documentElement.dir = direction
    document.documentElement.lang = locale.value
  }

  // Keep document direction in sync with locale changes
  watch(locale, (newLocale) => {
    applyDir(RTL_LOCALES.includes(newLocale) ? 'rtl' : 'ltr')
  })

  // Apply on store creation
  applyDir(dir.value)

  return {
    locale,
    isRtl,
    dir,
    setLocale,
  }
})
